<template>
  <div class="admin-page">
    <el-card shadow="never">
<p class="desc">
        从服务器配置读取 MySQL 连接信息，执行 mysqldump 并打包为 ZIP 下载。仅超级管理员可用。
      </p>

      <el-alert
        v-if="!isSuperAdmin"
        type="warning"
        title="当前账号无权限"
        description="需要超级管理员（superadmin）角色才能下载数据库备份。"
        show-icon
        :closable="false"
      />

      <div v-else class="actions">
        <el-button type="primary" :loading="downloadingFull" @click="downloadBackup('/api/v1/mysql/backup', 'full')">
          下载全量备份（ZIP）
        </el-button>
        <el-button type="success" :loading="downloadingSchema" @click="downloadBackup('/api/v1/mysql/backup/schema', 'schema')">
          下载结构备份（ZIP）
        </el-button>
        <p class="hint">结构备份仅包含建表语句、存储过程等，不含数据行。</p>
        <p v-if="lastFile" class="hint">上次下载：{{ lastFile }}</p>

        <el-divider content-position="left">备份日志（末尾 100 行）</el-divider>
        <div class="log-toolbar">
          <el-button size="small" :loading="loadingLogs" @click="fetchBackupLogs">刷新日志</el-button>
          <span v-if="logPath" class="hint">文件：{{ logPath }}</span>
        </div>
        <pre class="log-panel" v-loading="loadingLogs">{{ logText || '暂无日志' }}</pre>
      </div>
    </el-card>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { isSuperAdmin as checkSuperAdmin } from '@/utils/sysRole'

export default {
  name: 'DatabaseBackupView',
  setup() {
    const downloadingFull = ref(false)
    const downloadingSchema = ref(false)
    const lastFile = ref('')
    const loadingLogs = ref(false)
    const logLines = ref([])
    const logPath = ref('')

    const logText = computed(() => logLines.value.join('\n'))

    const isSuperAdmin = computed(() => {
      try {
        const user = JSON.parse(localStorage.getItem('user_info') || '{}')
        return checkSuperAdmin(user.sysRole)
      } catch {
        return false
      }
    })

    const downloadBackup = async (url, kind) => {
      if (!isSuperAdmin.value) {
        ElMessage.warning('需要超级管理员权限')
        return
      }

      const loadingRef = kind === 'schema' ? downloadingSchema : downloadingFull
      loadingRef.value = true
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 600000
        })

        const disposition = response.headers['content-disposition'] || ''
        let filename = kind === 'schema'
          ? `coolpeople_schema_${Date.now()}.zip`
          : `coolpeople_backup_${Date.now()}.zip`
        const match = disposition.match(/filename="?([^";\n]+)"?/)
        if (match) {
          filename = decodeURIComponent(match[1])
        }

        if (!response.data || response.data.size === 0) {
          ElMessage.error('备份文件大小为 0，请查看下方日志')
          await fetchBackupLogs()
          return
        }

        const blob = new Blob([response.data], { type: 'application/zip' })
        const objectUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = objectUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(objectUrl)

        lastFile.value = filename
        ElMessage.success(kind === 'schema' ? '结构备份下载已开始' : '全量备份下载已开始')
      } catch (error) {
        console.error('backup download failed:', error)
        if (error.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text()
            const json = JSON.parse(text)
            ElMessage.error(json.message || '备份失败')
            return
          } catch {
            // ignore
          }
        }
        ElMessage.error(error.response?.data?.message || error.message || '备份下载失败')
      } finally {
        loadingRef.value = false
      }
    }

    const fetchBackupLogs = async () => {
      if (!isSuperAdmin.value) return
      loadingLogs.value = true
      try {
        const token = localStorage.getItem('jwt_token')
        const { data } = await axios.get('/api/v1/mysql/backup/logs', {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (data.code === 200 && data.data) {
          logLines.value = data.data.lines || []
          logPath.value = data.data.logPath || ''
        } else {
          ElMessage.error(data.message || '读取日志失败')
        }
      } catch (error) {
        console.error('fetch backup logs failed:', error)
        ElMessage.error(error.response?.data?.message || '读取日志失败')
      } finally {
        loadingLogs.value = false
      }
    }

    onMounted(() => {
      if (!isSuperAdmin.value) {
        ElMessage.warning('需要超级管理员权限访问此页面')
      } else {
        fetchBackupLogs()
      }
    })

    return {
      downloadingFull,
      downloadingSchema,
      lastFile,
      loadingLogs,
      logLines,
      logPath,
      logText,
      isSuperAdmin,
      downloadBackup,
      fetchBackupLogs
    }
  }
}
</script>

<style scoped>

.database-backup {
  max-width: 720px;
}
.card-header {
  font-weight: 600;
}
.desc {
  color: var(--el-text-color-regular);
  line-height: 1.6;
  margin: 0 0 20px;
}
.actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}
.hint {
  margin: 0;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.log-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.log-panel {
  margin: 0;
  padding: 12px;
  max-height: 360px;
  overflow: auto;
  background: #1e1e1e;
  color: #d4d4d4;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
}

</style>
